import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Button } from "antd";
import { Sparkles } from "lucide-react";
import { ReactComponent as EyeIcon } from "../../../assets/svgs/eye.svg";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../../components/Elements/Search/SearchBar";
import { Tables } from "../../../components/Elements/Tables/Tables";
import AddNewDynamicForm from "../DailogBoxes/AddNewDynamicForm";
import { getApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import dayjs from "dayjs";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";

const DynamicForm = ({
  setOpenAssignedForm,
  projectId,
  clientId,
  setSelectedRole,
  setAssignedFormtype,
  projectSetup,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [openAddDynamicForm, setOpenAddDynamicForm] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [formsId, setFormsId] = useState([]);

  const [popup, setPopup] = useState({
    visible: false,
    type: "",
    title: "",
    message: "",
  });

  const [failedMsg, setFailedMsg] = useState(false);

  const fetchTableData = useCallback(
    async (projectId) => {
      try {
        const response = await getApi({
          apiUrl: API_URLS.GET_CUSTOM_FORM_BY_PROJECTID(projectId),
        });
        if (response?.success) {
          const data = response.data.map((item, index) => ({
            key: item._id,
            serial: index + 1,
            formName: item.title,
            assignedOn: dayjs(item.createdAt).format("DD/MM/YYYY"),
            action: (
              <div className="flex gap-3">
                <EyeIcon
                  style={{ fontSize: "18px", cursor: "pointer" }}
                  onClick={() => {
                    navigate(
                      `/dynamic-forms/${item._id}/all-questions?clientId=${clientId}`
                    );
                  }}
                />
              </div>
            ),
          }));
          setTableData(data);
        }
      } catch (error) {
        console.error("Error fetching dynamic forms:", error);
      }
    },
    [clientId, navigate, projectSetup]
  );

  // Table headers
  const surveyHeader = useMemo(
    () => [
      { label: "Sr. No.", key: "serial" },
      { label: "Form Name", key: "formName" },
      { label: "Assigned On", key: "assignedOn" },
      { label: "Action", key: "action" },
    ],
    []
  );

  // Search functionality
  const handleSearch = useCallback((searchTerm) => {
    setSearchTerm(searchTerm.toLowerCase());
  }, []);

  // Sorting functionality
  const handleSort = useCallback((key, order) => {
    setSortBy(key);
    setSortOrder(order);
  }, []);

  // Filtered data based on search term (unused but kept for future reference / compilation)
  const filteredData = useMemo(() => {
    if (!searchTerm) return tableData;
    return tableData.filter((item) =>
      surveyHeader.some((header) =>
        item[header.key]?.toString().toLowerCase().includes(searchTerm)
      )
    );
  }, [searchTerm, tableData, surveyHeader]);

  useEffect(() => {
    if (projectId) {
      fetchTableData(projectId);
    }
  }, [clientId, fetchTableData, openAddDynamicForm, projectSetup]);

  useEffect(() => {
    if (popup.visible) {
      const timer = setTimeout(() => {
        setPopup((prev) => ({ ...prev, visible: false }));
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [popup.visible]);

  return (
    <div>
      <div className="flex justify-between mx-8 mt-4 font-medium">
        <div className="flex gap-16">
          <p className="text-[#06513D] pt-2">DYNAMIC FORMS </p>
          <SearchBar width="300px" onSearch={handleSearch} />
        </div>
        <div className="flex gap-10 mb-2">
          <Button
            type="primary"
            style={{
              background: "#06513D",
              height: "50px",
              width: "150px",
              borderRadius: "10px",
            }}
            className="text-xs font-light hover:!bg-[#387464]"
            onClick={() => {
              setOpenAssignedForm(true);
              setAssignedFormtype("dynamic");
              setSelectedRole(null);
            }}
          >
            Assigned Form
          </Button>
          <Button
            type="primary"
            style={{
              background: "#06513D",
              height: "50px",
              width: "150px",
              borderRadius: "10px",
            }}
            className="text-xs font-light hover:!bg-[#387464]"
            onClick={() => {
              setOpenAddDynamicForm(true);
            }}
          >
            Add New
          </Button>
        </div>
      </div>

      <div className="mx-8 mb-6 mt-4">
        <Tables
          data={[]} // Render empty table so emptyText (Coming Soon placeholder) is shown
          headers={surveyHeader}
          pageSize={7}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          emptyText={
            <div className="py-12 flex flex-col items-center justify-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-[#06513D]/5 scale-150 animate-ping"></div>
                <div className="flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#E6EEEC] border border-[#06513D]/20">
                  <Sparkles className="w-10 h-10 sm:w-12 sm:h-12 text-[#06513D]" />
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#06513D] tracking-tight mb-3">
                Dynamic Forms Coming Soon
              </h2>
              <p className="max-w-md text-sm sm:text-base text-gray-500 leading-relaxed mb-6">
                We are currently building a fully customizable form building
                experience. Very soon, you'll be able to create, clone, and manage
                custom templates dynamically for all your projects.
              </p>

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E6EEEC] text-[#06513D] rounded-full text-xs font-semibold uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-[#0BB789] animate-pulse"></span>
                Feature Under Construction
              </div>
            </div>
          }
        />
      </div>

      {openAddDynamicForm && (
        <AddNewDynamicForm
          open={openAddDynamicForm}
          onCancel={() => {
            setOpenAddDynamicForm(false);
          }}
          currentForms={tableData}
          projectId={projectId}
          clientId={clientId}
          onSave={() => {
            setPopup({
              visible: true,
              type: "success",
              title: "Added Successfully",
              message: "Dynamic Form Added successfully",
            });
          }}
          saveFailed={() => {
            setPopup({
              visible: true,
              type: "error",
              title: "Failed !!",
              message: "Unable to Add Dynamic Form",
            });
          }}
          setFailedMsg={setFailedMsg}
        />
      )}

      {popup.visible && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
          />
        </div>
      )}
    </div>
  );
};

export default DynamicForm;
